import { MapPin, Clock, Phone } from 'lucide-react';
import Navigation from './Navigation';
import translations from './translations';

const RESTAURANT_INFO = {
  name: "Leung Noodle",
  locations: [
    {
      name: "Senter Road Location",
      address: "2859 Senter Rd, San Jose, CA, 95111",
      phone: "(408) 705-9848",
      hours: "Mon-Sun: 10:00 AM - 9:00 PM"
    },
    {
      name: "Silver Creek Location",
      address: "3005 Silver Creek Rd Ste #130, San Jose, CA, 95121",
      phone: "(408) 622-8352",
      hours: "Mon-Sun: 10:00 AM - 9:00 PM"
    }
  ]
};

const ContactUs = ({ language, toggleLanguage }) => {
  const t = translations[language];
  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #F5F1E8 0%, #EDE8DC 100%)',
      color: '#2d2d2d',
      fontFamily: '"Nunito", sans-serif'
    }}>
      {/* Navigation Bar */}
      <Navigation language={language} toggleLanguage={toggleLanguage} />

      {/* Contact Us Section */}
      <section style={{
        paddingTop: '100px',
        padding: '4rem 2rem'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
          <h2 style={{
            fontSize: 'clamp(2rem, 4vw, 2.5rem)',
            fontWeight: '300',
            textAlign: 'center',
            marginBottom: '3rem',
            color: '#3d3d3d',
            letterSpacing: '0.05em'
          }}>
            {t.contact.title}
          </h2>

          {/* Location cards */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 500px), 1fr))',
            gap: '2rem'
          }}>
            {RESTAURANT_INFO.locations.map((location, index) => (
              <div
                key={index}
                style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.8) 0%, rgba(245,241,232,0.6) 100%)',
                  border: '1px solid rgba(210,195,170,0.4)',
                  borderRadius: '20px',
                  padding: 'clamp(1rem, 4vw, 2rem)',
                  boxShadow: '0 10px 30px rgba(139, 119, 101, 0.1)',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = '0 15px 40px rgba(139, 119, 101, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 10px 30px rgba(139, 119, 101, 0.1)';
                }}
              >
                <h3 style={{
                  fontSize: '1.5rem',
                  fontWeight: '400',
                  marginBottom: '1.5rem',
                  color: '#3d3d3d'
                }}>
                  {index === 0 ? t.contact.senterLocation : t.contact.silverCreekLocation}
                </h3>

                {/* Address */}
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '0.75rem',
                  marginBottom: '1rem',
                  color: '#5d4e37',
                  fontFamily: '"Inter", sans-serif'
                }}>
                  <MapPin size={20} style={{ marginTop: '2px', flexShrink: 0 }} />
                  <a
                    href={`https://maps.google.com/?q=${encodeURIComponent(location.address)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: '#5d4e37',
                      textDecoration: 'none',
                      transition: 'color 0.3s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#C49A6C'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#5d4e37'}
                  >
                    {location.address}
                  </a>
                </div>

                {/* Phone */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  marginBottom: '1rem',
                  fontFamily: '"Inter", sans-serif'
                }}>
                  <Phone size={20} style={{ flexShrink: 0 }} />
                  <a
                    href={`tel:${location.phone}`}
                    style={{
                      color: '#5d4e37',
                      textDecoration: 'none',
                      transition: 'color 0.3s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#C49A6C'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#5d4e37'}
                  >
                    {location.phone}
                  </a>
                </div>

                {/* Hours */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  marginBottom: '1.5rem',
                  color: '#5d4e37',
                  fontFamily: '"Inter", sans-serif'
                }}>
                  <Clock size={20} style={{ flexShrink: 0 }} />
                  <span>{location.hours}</span>
                </div>

                {/* Google Maps Embed */}
                <div style={{
                  borderRadius: '12px',
                  overflow: 'hidden',
                  height: 'clamp(200px, 40vw, 300px)',
                  border: '1px solid rgba(210, 180, 140, 0.3)',
                  width: '100%'
                }}>
                  <iframe
                    title={`Map of ${location.name}`}
                    src={`https://www.google.com/maps/embed/v1/place?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&q=${encodeURIComponent(location.address)}`}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{
        borderTop: '1px solid rgba(210,195,170,0.3)',
        padding: '3rem 2rem',
        textAlign: 'center',
        background: 'linear-gradient(135deg, #F5F1E8 0%, #EDE8DC 100%)'
      }}>
        <p style={{
          color: '#8B7355',
          fontSize: '0.9rem',
          fontFamily: '"Inter", sans-serif'
        }}>
          © 2021 {RESTAURANT_INFO.name}. {t.home.footer}
        </p>
      </footer>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Crimson+Pro:wght@300;400;500;600&family=Inter:wght@300;400;500;600&family=Nunito:wght@300;400;500;600;700;800;900&display=swap');

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default ContactUs;
